import React from 'react';
import { Helmet } from 'react-helmet-async';

const Policy = () => {
  return (
    <>
      <Helmet>
        <title>Service Credits Terms - MemU</title>
        <meta name="description" content="MemU Service Credits Terms and Conditions - Learn about our credit system, usage policies, and terms of service for AI memory integration services." />
        <meta name="keywords" content="memu service credits, terms of service, credit policy, ai service terms, memory api credits" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Service Credits Terms - MemU" />
        <meta property="og:description" content="MemU Service Credits Terms and Conditions - Learn about our credit system, usage policies, and terms of service for AI memory integration services." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://memu.pro/policy" />
        <meta property="og:image" content="https://d192tm8h0ep0ud.cloudfront.net/memu.pro/static/memu-logo.png" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Service Credits Terms - MemU" />
        <meta name="twitter:description" content="MemU Service Credits Terms and Conditions - Learn about our credit system, usage policies, and terms of service for AI memory integration services." />
        <meta name="twitter:image" content="https://d192tm8h0ep0ud.cloudfront.net/memu.pro/static/memu-logo.png" />
      </Helmet>
      
      <div className="policy-page">
        <div className="container">
          <div className="policy-content">
            <header className="policy-header">
              <h1>MemU Service Credits Terms</h1>
              <p className="policy-subtitle">Effective Date: October 1, 2025</p>
            </header>

            <div className="policy-body">
              <p className="intro-text">
                These MemU Service Credits Terms ("Terms") constitute an agreement between you and MemU Technologies, Inc. regarding your purchase, acquisition, or use of credits that may be redeemed for MemU services ("Service Credits"). Terms appearing in these Terms but not defined herein have the meaning set forth in the agreement between you and MemU regarding your use of the applicable service ("Master Agreement").
              </p>

              <section className="policy-section">
                <h2>1. Service Credits Overview</h2>
                <p>
                  You may choose to purchase credits ("Prepaid Service Credits") to pre-purchase specific services. Prepaid Service Credits represent amounts you have prepaid for the use of applicable services. Additional terms may apply to specific services.
                </p>
                <p>
                  As part of promotional programs, we may also provide Service Credits to you at no charge (rather than for purchase) ("Promotional Service Credits"). Promotional Service Credits may not be applied toward any applicable sales tax, use tax, excise tax, or similar transaction-based tax charges.
                </p>
              </section>

              <section className="policy-section">
                <h2>2. Nature of Service Credits</h2>
                <p>
                  Service Credits are not legal tender or currency; cannot be redeemed, refunded, or exchanged for any monetary amount or monetary value (except as required by law); do not have any equivalent monetary value; cannot be used as a substitute for legal tender; and do not constitute or confer any personal property right. Service Credits are non-transferable and are only for use with the applicable services themselves.
                </p>
              </section>

              <section className="policy-section">
                <h2>3. Non-Refundability and Expiration</h2>
                <p>
                  All service sales (including prepaid service sales) are final. Service Credits are non-refundable (except as required by law) and, if unused, will expire one year after the date of purchase or issuance, unless otherwise specified at the time of purchase.
                </p>
              </section>

              <section className="policy-section">
                <h2>4. Account Balance and Verification</h2>
                <p>
                  You may view your available Service Credits balance in your MemU account or workspace (depending on the specific service). You are responsible for verifying that the number of Service Credits added to or deducted from your balance is accurate. Service Credits balance is not a bank account, digital wallet, stored value account, or other payment instrument.
                </p>
              </section>

              <section className="policy-section">
                <h2>5. Prohibited Activities</h2>
                <p>
                  Any attempted assignment, sale, gift, or trade of Service Credits is prohibited and will not be recognized by us. Evidence of any attempt to use, sell, or transfer Service Credits in any manner constitutes a violation of these Terms and may result in revocation, termination, or voiding of the applicable Service Credits, and/or result in your inability to use the applicable service without refund, and/or result in the immediate temporary or permanent suspension of your account.
                </p>
              </section>

              <section className="policy-section">
                <h2>6. Service Suspension and Termination</h2>
                <p>
                  Subject to the Master Agreement and applicable law, we reserve the right to suspend or terminate your use of the services, including suspending or terminating your Service Credits balance.
                </p>
              </section>

              <section className="policy-section">
                <h2>7. Usage Monitoring and Fair Use</h2>
                <p>
                  We monitor the usage of our AI memory services to ensure fair access for all users. Excessive or abusive usage that significantly impacts system performance or violates our acceptable use policies may result in temporary rate limiting, account review, or service restrictions.
                </p>
              </section>

              <section className="policy-section">
                <h2>8. API Rate Limits and Service Availability</h2>
                <p>
                  Service Credits are subject to applicable API rate limits and service availability. Rate limits may vary based on your subscription tier and current system capacity. We strive to maintain high service availability but cannot guarantee uninterrupted service.
                </p>
              </section>

              <section className="policy-section">
                <h2>9. Data Processing and Memory Storage</h2>
                <p>
                  Service Credits used for memory services include costs associated with storing, processing, and retrieving your data. Data retention periods and processing capabilities may vary based on your service plan. We implement industry-standard security measures to protect your data.
                </p>
              </section>

              <section className="policy-section">
                <h2>10. Amendments to Terms</h2>
                <p>
                  We reserve the right to amend these Terms at any time. Amended versions will be posted on our website or sent to the email address associated with your account. Amended versions become effective when posted on our website or, if emailed to you, on the date specified in the email. Your continued acquisition or use of any Service Credits after the effective date of any amendment to these Terms indicates your agreement to be bound by the amended Terms.
                </p>
              </section>

              <section className="policy-section">
                <h2>11. Enterprise and Custom Plans</h2>
                <p>
                  Customers with enterprise or custom service agreements may have different credit terms, usage limits, or billing arrangements as specified in their individual contracts. Contact our sales team for enterprise-specific terms and conditions.
                </p>
              </section>

              <section className="policy-section">
                <h2>12. Contact Information</h2>
                <p>
                  If you have questions about these Service Credits Terms or need assistance with your account, please contact us through our support channels or email us at info@nevamind.ai.
                </p>
              </section>

              <footer className="policy-footer">
                <p>
                  <strong>Last Updated:</strong> October 1, 2025
                </p>
                <p>
                  These terms supplement and are subject to the MemU Master Service Agreement and Privacy Policy.
                </p>
              </footer>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .policy-page {
          min-height: 100vh;
          padding: 150px 0 40px;
          background: var(--light);
          color: var(--dark);
        }

        .policy-content {
          max-width: 800px;
          margin: 0 auto;
          padding: 0 20px;
        }

        .policy-header {
          margin-bottom: 40px;
          text-align: center;
          padding-bottom: 20px;
          border-bottom: 2px solid var(--primary);
        }

        .policy-header h1 {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 10px;
          background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .policy-subtitle {
          font-size: 1.1rem;
          color: var(--dark-60);
          font-weight: 500;
        }

        .intro-text {
          font-size: 1.1rem;
          line-height: 1.8;
          margin-bottom: 30px;
          padding: 20px;
          background: var(--light-blue);
          border-radius: 8px;
          border-left: 4px solid var(--primary);
        }

        .policy-section {
          margin-bottom: 30px;
          padding: 25px 0;
          border-bottom: 1px solid var(--gray);
        }

        .policy-section:last-child {
          border-bottom: none;
        }

        .policy-section h2 {
          font-size: 1.4rem;
          font-weight: 600;
          margin-bottom: 15px;
          color: var(--primary-dark);
        }

        .policy-section p {
          font-size: 1rem;
          line-height: 1.7;
          margin-bottom: 15px;
          color: var(--dark-80);
        }

        .policy-section p:last-child {
          margin-bottom: 0;
        }

        .policy-footer {
          margin-top: 40px;
          padding-top: 30px;
          border-top: 2px solid var(--primary);
          text-align: center;
        }

        .policy-footer p {
          font-size: 0.95rem;
          color: var(--dark-60);
          margin-bottom: 10px;
        }

        .policy-footer p:last-child {
          margin-bottom: 0;
        }

        /* Mobile Responsiveness */
        @media (max-width: 768px) {
          .policy-page {
            padding: 60px 0 30px;
          }

          .policy-content {
            padding: 0 15px;
          }

          .policy-header h1 {
            font-size: 2rem;
          }

          .intro-text {
            font-size: 1rem;
            padding: 15px;
          }

          .policy-section {
            padding: 20px 0;
          }

          .policy-section h2 {
            font-size: 1.2rem;
          }

          .policy-section p {
            font-size: 0.95rem;
          }
        }
      `}</style>
    </>
  );
};

export default Policy;
